import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Globe, ChevronDown } from "lucide-react";
import Logo from "./Logo";
import { useI18n } from "../i18n";

export default function Header() {
  const { lang, t } = useI18n();
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setOpen(false);
    setLangOpen(false);
  }, [location.pathname]);

  const base = `/${lang}`;

  const nav = [
    { to: `${base}#overview`, label: t("nav.overview") },
    { to: `${base}#process`, label: t("nav.process") },
    { to: `${base}#transparency`, label: t("nav.transparency") },
    { to: `${base}#contact`, label: t("nav.contact") },
  ];

  const switchLang = (target) => {
    const parts = location.pathname.split("/").filter(Boolean);
    if (parts.length && (parts[0] === "ar" || parts[0] === "en")) {
      parts[0] = target;
    } else {
      parts.unshift(target);
    }
    const nextPath = "/" + parts.join("/");
    navigate(nextPath + location.hash, { replace: false });
    setLangOpen(false);
  };

  const goAnchor = (hash) => {
    setOpen(false);
    if (location.pathname !== base && location.pathname !== `${base}/`) {
      navigate(`${base}${hash}`);
      return;
    }
    const id = hash.replace("#", "");
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header className="site-header" data-testid="site-header">
      <a href="#main-content" className="skip-link">{t("a11y.skip")}</a>
      <div className="container header-inner">
        <NavLink to={base} className="header-brand" aria-label={t("a11y.logo")} data-testid="header-logo">
          <Logo />
        </NavLink>

        <nav className="header-nav" aria-label={t("nav.menu")}>
          {nav.map((item) => (
            <button
              key={item.to}
              type="button"
              className="header-nav__link"
              onClick={() => goAnchor(item.to.replace(base, ""))}
              data-testid={`nav-${item.label}`}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="header-actions">
          <div className="lang-switch">
            <button
              type="button"
              className="lang-switch__button"
              aria-haspopup="listbox"
              aria-expanded={langOpen}
              onClick={() => setLangOpen((v) => !v)}
              data-testid="lang-switch"
            >
              <Globe size={16} aria-hidden="true" />
              <span>{lang === "ar" ? "ES" : "EN"}</span>
              <ChevronDown size={14} aria-hidden="true" />
            </button>
            {langOpen && (
              <ul className="lang-switch__menu" role="listbox">
                <li>
                  <button type="button" onClick={() => switchLang("ar")} aria-current={lang === "ar"} data-testid="lang-es">
                    Español (AR)
                  </button>
                </li>
                <li>
                  <button type="button" onClick={() => switchLang("en")} aria-current={lang === "en"} data-testid="lang-en">
                    English
                  </button>
                </li>
              </ul>
            )}
          </div>

          <button
            type="button"
            className="burger"
            aria-label={open ? t("nav.close") : t("nav.open")}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            data-testid="burger-button"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="mobile-menu" role="dialog" aria-modal="true">
          <div className="container">
            <ul className="mobile-menu__list">
              {nav.map((item) => (
                <li key={item.to}>
                  <button type="button" onClick={() => goAnchor(item.to.replace(base, ""))}>
                    {item.label}
                  </button>
                </li>
              ))}
              <li className="mobile-menu__divider" aria-hidden="true" />
              <li>
                <NavLink to={`${base}/privacy`}>{t("nav.privacy")}</NavLink>
              </li>
              <li>
                <NavLink to={`${base}/cookies`}>{t("nav.cookies")}</NavLink>
              </li>
              <li>
                <NavLink to={`${base}/terms`}>{t("nav.terms")}</NavLink>
              </li>
            </ul>
          </div>
        </div>
      )}
    </header>
  );
}
